import type { FC, SVGProps } from 'react'

import { useUnit } from 'effector-react'
import upperFirst from 'lodash/upperFirst'
import { useQuery } from 'react-query'

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tooltip } from '@/components/ui/tooltip'
import { $account, $isNSFWOn, setIsNSFWOn } from '@/models/account.ts'
import { $currentCreator } from '@/models/creators.ts'
import DescriptionIcon from '@/modules/chatting/icons/description.svg'
import LogoBlack from '@/modules/chatting/icons/logo-black.svg'
import LogoWhite from '@/modules/chatting/icons/logo-white.svg'
import TextLogoBlack from '@/modules/chatting/icons/text-logo-black.svg'
import TextLogoWhite from '@/modules/chatting/icons/text-logo-white.svg'
import TooltipIcon from '@/modules/chatting/icons/tooltip.svg'
import { useSettingsContext } from '@/modules/creator-settings/state/SettingsProvider'
import { billingService } from '@/modules/licenses/service/billing-service'
import { useIsDarkTheme } from '@/utils/useTheme'

import SupportIcon from '../../icons/ch-popup-support.svg'
import TelegramIcon from '../../icons/ch-popup-telegram.svg'
import HighlightNumberedFansIcon from '../../icons/highlight-numbered-fans.svg'
import ProhibitionIcon from '../../icons/prohibition.svg'
import { useChatting } from '../../state/chatting-provider'
import { IconButton } from '../components/icon-button'

export const defaultHighlightOption = {
  id: -1,
  name: 'Off',
}

export const CreatorHeroSettingsPopup = () => {
  const isDark = useIsDarkTheme()
  const creator = useUnit($currentCreator)
  const isNSFWOn = useUnit($isNSFWOn)

  const {
    highlightedNumber,
    setHighlightedNumber,
    isScriptAutoSuggestionEnabled,
    setIsScriptAutoSuggestionEnabled,
  } = useChatting()
  const account = useUnit($account)
  const { maxFanNumber } = useSettingsContext()
  const { data: plans } = useQuery(['billing'], billingService.getPlans)

  const newOptions = Array.from(
    { length: maxFanNumber! },
    (_number, index) => ({
      id: index + 1,
      name: index + 1,
    }),
  )
  const finalOptions = [defaultHighlightOption, ...newOptions]

  const planDisplayName = plans?.find(
    (p) => p.name === creator?.plan.split('_')[0],
  )?.displayName
  const planVariantName = creator?.plan.split('_')[1]

  const handleChangeHighlightedNumber = (value: string) =>
    setHighlightedNumber(Number(value))

  return (
    <Popover>
      <PopoverTrigger asChild>
        <IconButton
          icon={isDark ? LogoWhite : LogoBlack}
          iconClassName="h-8 w-8 text-white hover:text-primary-500"
          tooltip="CreatorHero Settings"
        />
      </PopoverTrigger>
      <PopoverContent className="z-[51] flex w-max flex-col items-stretch border-none sm:w-[420px]">
        <div className="flex flex-col items-center space-y-3">
          {isDark ? (
            <TextLogoWhite className="h-6" />
          ) : (
            <TextLogoBlack className="h-6" />
          )}
          {planDisplayName && (
            <p className="text-sm">
              Your license:{' '}
              <span className="font-semibold">
                {planDisplayName}
                {planVariantName && ` (${upperFirst(planVariantName)}`})
              </span>
            </p>
          )}
        </div>
        <div className="mt-9 space-y-4 rounded-xl border p-3">
          <Setting
            label="NSFW Filter"
            icon={ProhibitionIcon}
            action={
              <Switch
                checked={!!isNSFWOn}
                disabled={account?.nsfwAlwaysOn}
                onCheckedChange={setIsNSFWOn}
                className="border-none"
              />
            }
          />
          <Setting
            label="Script Suggestions"
            icon={DescriptionIcon}
            tooltip="This enables automatic script suggestions while typing in the input bar."
            action={
              <Switch
                checked={isScriptAutoSuggestionEnabled}
                onCheckedChange={setIsScriptAutoSuggestionEnabled}
                className="border-none"
              />
            }
          />
          <Setting
            label="Highlight Numbered Fans"
            icon={HighlightNumberedFansIcon}
            action={
              <Select
                value={String(highlightedNumber)}
                onValueChange={handleChangeHighlightedNumber}
              >
                <SelectTrigger className="w-max bg-transparent">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {finalOptions.map(({ id, name }) => (
                    <SelectItem
                      value={String(id)}
                      key={String(id)}
                    >
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
          />
        </div>
        <div className="mt-6 flex gap-3">
          <div
            onClick={() => {
              window.open('https://help.creatorhero.com/en/', '_blank')
            }}
            className="hover:bg-secondary-50 dark:hover:bg-secondary-500 flex flex-1 cursor-pointer justify-center gap-1 rounded border px-4 py-2 text-center"
          >
            <SupportIcon />
            <span>Support</span>
          </div>
          <div
            onClick={() => {
              window.open('https://t.me/creatorhero', '_blank')
            }}
            className="hover:bg-secondary-50 dark:hover:bg-secondary-500 flex flex-1 cursor-pointer justify-center gap-1 rounded border px-4 py-2 text-center"
          >
            <TelegramIcon />
            <span>Telegram</span>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface SettingProps {
  label: string
  action: JSX.Element
  icon?: FC<SVGProps<SVGElement>>
  tooltip?: string
}
const Setting = ({ label, action, icon: Icon, tooltip }: SettingProps) => {
  return (
    <div className="bg-secondary-50 dark:bg-secondary-600 flex w-full items-center justify-between gap-4 rounded px-4 py-5">
      <div className="flex items-center gap-2.5">
        {Icon && <Icon />}
        <div className="flex items-center gap-1.5">
          <span className="font-medium">{label}</span>
          {tooltip && (
            <Tooltip
              content={tooltip}
              contentClassName="max-w-[300px]"
            >
              <TooltipIcon className="text-secondary-300 h-4 w-4" />
            </Tooltip>
          )}
        </div>
      </div>
      {action}
    </div>
  )
}
